/**
 * TappingPointsAddEditPageV2 — V2 edit-page chrome for Tapping Points.
 */
import React, { useCallback, useState } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import AddEditTappingPointsV2 from '../../AddEditTappingPoints/v2/AddEditTappingPointsV2';
import { Button, cn } from '../../redesign';

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

export default function TappingPointsAddEditPageV2() {
  const location = useLocation();
  const params = useParams();
  const isEdit = location.pathname.includes('edit');

  const [tpName, setTpName] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const handleFormLoaded = useCallback(({ tapping_point_name, tapping_point_updated_at }) => {
    setTpName(tapping_point_name || null);
    setLastSavedAt(tapping_point_updated_at || null);
  }, []);

  const lastSavedLabel = lastSavedAt
    ? `last saved ${new Date(lastSavedAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })}`
    : null;

  const eyebrow = isEdit ? 'Editing tapping point' : 'New tapping point';
  const title = isEdit ? (tpName || `Tapping point #${params.id}`) : 'Untitled tapping point';

  return (
    <div className="bg-bg min-h-full">
      <div
        className="sticky top-0 bg-bg border-b border-sand"
        style={{ zIndex: 20 }}
      >
        <div
          className="flex items-start"
          style={{ padding: '20px 28px', gap: 16, flexWrap: 'wrap' }}
        >
          <div className="flex-1 min-w-0">
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 11, letterSpacing: '0.08em' }}
            >
              {eyebrow}
            </div>
            <h1
              className={cn(
                'text-navy truncate',
                !tpName && isEdit && 'text-text-light italic',
              )}
              style={{
                fontSize: 28,
                fontWeight: 700,
                margin: '4px 0 4px',
                letterSpacing: '-0.015em',
                lineHeight: 1.15,
              }}
              title={title}
            >
              {title}
            </h1>
            <div className="font-mono text-text-muted" style={{ fontSize: 12 }}>
              {isEdit ? (
                <>
                  tapping_point_id · <strong className="text-navy">{params.id}</strong>
                  {lastSavedLabel && (
                    <>
                      {' · '}
                      <span>{lastSavedLabel}</span>
                    </>
                  )}
                </>
              ) : (
                <span>Fill the fields below and click Create.</span>
              )}
            </div>
          </div>

          <div className="flex items-center" style={{ gap: 8 }}>
            <Link to="/tapping-points" className="no-underline">
              <Button variant="ghost">Discard Changes</Button>
            </Link>
            <Button
              variant="coral"
              type="submit"
              form="tapping-point-form"
              leadingIcon={SaveIconSvg}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>

      <div style={{ padding: '24px 32px 60px' }}>
        <AddEditTappingPointsV2
          isEdit={isEdit}
          selectedId={params.id}
          onLoaded={handleFormLoaded}
        />
      </div>
    </div>
  );
}
